import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Loader, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { CloudMark, Wordmark } from '../components/CloudFrugalLogo';

interface InitiateResponse {
  token: string;
  externalId: string;
  cloudFormationUrl: string;
}

interface PollResponse {
  status: 'pending' | 'completed' | 'expired';
  accountId?: string;
}

export default function ConnectAccount() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'info' | 'iam' | 'waiting'>('info');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [cloudFormationUrl, setCloudFormationUrl] = useState('');
  const pollIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pollStatus, setPollStatus] = useState<'pending' | 'completed' | 'expired'>('pending');
  const [pollError, setPollError] = useState('');

  // Step to IAM - initiate linking
  const handleStartLinking = async () => {
    setError('');
    setLoading(true);

    try {
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        navigate('/login');
        return;
      }

      const response = await axios.post<InitiateResponse>(
        `${import.meta.env.VITE_API_BASE_URL}/api/aws-accounts/initiate`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      const { token: newToken, cloudFormationUrl: cfnUrl } = response.data;
      setToken(newToken);
      setCloudFormationUrl(cfnUrl);
      setStep('iam');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to initiate AWS account linking');
    } finally {
      setLoading(false);
    }
  };

  // Step to Waiting - user has deployed the stack, start polling
  const handleStackDeployed = () => {
    setStep('waiting');
    setPollStatus('pending');
    setPollError('');
    startPolling();
  };

  // Poll for account linking completion
  const startPolling = () => {
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      navigate('/login');
      return;
    }

    // Poll immediately, then every 3 seconds
    const poll = async () => {
      try {
        const response = await axios.get<PollResponse>(
          `${import.meta.env.VITE_API_BASE_URL}/api/aws-accounts/poll/${token}`,
          {
            headers: { Authorization: `Bearer ${authToken}` }
          }
        );

        const { status } = response.data;
        setPollStatus(status);

        if (status === 'completed') {
          // Clear interval and redirect
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          setTimeout(() => navigate('/dashboard'), 1500);
        } else if (status === 'expired') {
          setPollError('Session expired. Please start over.');
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
        }
      } catch (err: any) {
        setPollError('Failed to check linking status');
      }
    };

    poll(); // Poll immediately
    pollIntervalRef.current = setInterval(poll, 3000); // Then every 3 seconds
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Handle back from waiting step
  const handleBackFromWaiting = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    setStep('iam');
    setPollStatus('pending');
    setPollError('');
  };

  return (
    <div className="min-h-screen py-12 px-4" style={{ background: "linear-gradient(135deg, #07281D 0%, #0F3D2E 100%)" }}>
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <CloudMark size={56} />
              <Wordmark size={15} />
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">Connect Your AWS Account</h1>
          <p className="text-gray-600 mb-8">
            Securely link your AWS account using a read-only IAM role. Takes less than 2 minutes.
          </p>

          {/* Progress Steps */}
          <div className="mb-8 flex justify-between">
            <div
              className={`flex-1 flex items-center ${step !== 'info' ? 'cursor-pointer' : ''}`}
              onClick={() => step !== 'info' && setStep('info')}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm mr-3 ${
                  step === 'info' || step === 'iam' || step === 'waiting'
                    ? 'bg-[#0F3D2E] text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                1
              </div>
              <span className="hidden sm:inline text-sm font-medium">Information</span>
            </div>
            <div className={`h-1 bg-gray-200 flex-1 mx-3 my-5 ${step === 'iam' || step === 'waiting' ? 'bg-[#0F3D2E]' : ''}`} />
            <div
              className={`flex-1 flex items-center ${step === 'iam' || step === 'waiting' ? 'cursor-pointer' : ''}`}
              onClick={() => (step === 'iam' || step === 'waiting') && setStep('iam')}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm mr-3 ${
                  step === 'iam' || step === 'waiting' ? 'bg-[#0F3D2E] text-white' : 'bg-gray-200 text-gray-600'
                }`}
              >
                2
              </div>
              <span className="hidden sm:inline text-sm font-medium">Create Role</span>
            </div>
            <div className={`h-1 bg-gray-200 flex-1 mx-3 my-5 ${step === 'waiting' ? 'bg-[#0F3D2E]' : ''}`} />
            <div className="flex-1 flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm mr-3 ${
                  step === 'waiting' ? 'bg-[#0F3D2E] text-white' : 'bg-gray-200 text-gray-600'
                }`}
              >
                3
              </div>
              <span className="hidden sm:inline text-sm font-medium">Link Account</span>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Info */}
          {step === 'info' && (
            <div className="space-y-6">
              <div className="bg-[#e8f0ec] rounded-lg p-6">
                <h3 className="font-bold text-emerald-900 mb-2">How it works</h3>
                <ul className="text-sm text-emerald-800 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>CloudFrugal uses a secure cross-account IAM role to access your AWS resources</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>The role has read-only permissions for Cost Explorer, EC2, RDS, and other services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>No credentials are stored - we only use the IAM role ARN</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>You can revoke access anytime by deleting the IAM role</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={handleStartLinking}
                disabled={loading}
                className="w-full py-3 rounded-lg font-semibold hover:brightness-110 transition-all disabled:opacity-50 flex items-center justify-center gap-2" style={{ background: "#E8B84C", color: "#07281D" }}
              >
                {loading ? 'Preparing...' : 'Continue'}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 rounded-lg text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
              >
                Cancel — go back to Dashboard
              </button>
            </div>
          )}

          {/* Step 2: One-Click Deployment */}
          {step === 'iam' && (
            <div className="space-y-6">
              <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                <h3 className="font-bold text-green-900 mb-2">✓ All Set!</h3>
                <p className="text-sm text-green-800">
                  Everything is pre-configured. All you need to do is click the button below to deploy the AWS CloudFormation stack.
                </p>
              </div>

              <div className="bg-[#e8f0ec] rounded-lg p-4 border border-[#9ec5ae]">
                <p className="text-sm text-emerald-800">
                  <strong>What happens:</strong>
                </p>
                <ul className="text-sm text-emerald-800 space-y-1 mt-2 list-disc list-inside">
                  <li>You'll be taken to AWS CloudFormation</li>
                  <li>All parameters are pre-filled (just review and click Create)</li>
                  <li>The stack will deploy your CloudFrugal IAM role</li>
                  <li>CloudFrugal will automatically detect the deployment and link your account</li>
                </ul>
              </div>

              <a
                href={cloudFormationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-orange-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-orange-700 transition-colors text-center shadow-lg hover:shadow-xl"
              >
                → Deploy to AWS (Single-Click)
              </a>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="font-bold text-gray-900 mb-4">After Deployment</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Once you've clicked "Create Stack" in AWS, click the button below to wait for the account linking to complete.
                </p>
              </div>

              <button
                onClick={handleStackDeployed}
                className="w-full py-3 rounded-lg font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-2" style={{ background: "#E8B84C", color: "#07281D" }}
              >
                I've Started the Deployment <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Step 3: Waiting for Account Linking */}
          {step === 'waiting' && (
            <div className="space-y-6">
              <div className="text-center">
                {pollStatus === 'pending' && (
                  <>
                    <Loader className="w-16 h-16 text-emerald-800 animate-spin mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Linking Your Account...</h2>
                    <p className="text-gray-600 mb-2">
                      The CloudFormation stack is being processed.
                    </p>
                    <p className="text-sm text-gray-500">
                      This usually takes 1-2 minutes. We'll automatically redirect you once complete.
                    </p>
                  </>
                )}

                {pollStatus === 'completed' && (
                  <>
                    <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Account Linked Successfully!</h2>
                    <p className="text-gray-600">
                      Your AWS account has been securely linked to CloudFrugal. Redirecting to your dashboard...
                    </p>
                  </>
                )}

                {pollStatus === 'expired' && (
                  <>
                    <div className="w-16 h-16 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-3xl mx-auto mb-4">
                      ⚠
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Session Expired</h2>
                    <p className="text-gray-600 mb-6">{pollError || 'Your linking session has expired.'}</p>
                    <button
                      onClick={() => setStep('info')}
                      className="w-full py-3 rounded-lg font-semibold hover:brightness-110 transition-all" style={{ background: "#E8B84C", color: "#07281D" }}
                    >
                      Start Over
                    </button>
                  </>
                )}
              </div>

              {pollStatus === 'pending' && (
                <>
                  <div className="bg-[#e8f0ec] rounded-lg p-4 border border-[#9ec5ae]">
                    <p className="text-sm text-emerald-800">
                      <strong>What's happening:</strong> We're checking the CloudFormation stack status and validating the IAM role you created.
                    </p>
                  </div>

                  <button
                    onClick={handleBackFromWaiting}
                    className="w-full bg-gray-100 text-gray-900 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Back
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
